/**
 * Socket hook cho RPS tournament.
 * Gọi sau khi đã mount useGameSocket (dùng chung socket connection).
 */
import { useEffect, useCallback } from 'react';
import { getSocket } from './client';
import { SERVER_EVENTS, CLIENT_EVENTS } from './events';
import { rpsStore } from '@/store/rpsStore';
import { gameStore } from '@/store/gameStore';
import { sessionStore } from '@/store/sessionStore';

export function useRpsSocket(roundId: string) {
  useEffect(() => {
    const token = sessionStore.getState().token;
    if (!token) return;

    const socket = getSocket(token);

    const onBracketDrawStarted = (raw: any) => {
      rpsStore.getState().setBracketDrawStarted({
        tournament_round: raw.tournament_round,
        round_label: raw.round_label,
        participant_count: raw.participant_count,
      });
    };

    const onTournamentRoundStarted = (raw: any) => {
      const myParticipantId = gameStore.getState().myParticipantId;
      rpsStore.getState().setTournamentRoundStarted({
        tournament_round: raw.tournament_round,
        round_label: raw.round_label,
        participant_count: raw.participant_count,
        matches: raw.matches,
        timer_sec: raw.timer_sec,
        match_format: raw.match_format,
        myParticipantId,
      });
    };

    const onSubRoundStarted = (raw: any) => {
      rpsStore.getState().setSubRoundStarted({
        match_id: raw.match_id,
        sub_round: raw.sub_round,
        started_at: raw.started_at,
        timer_sec: raw.timer_sec,
        player1_games_won: raw.player1_games_won,
        player2_games_won: raw.player2_games_won,
      });
    };

    const onPlayerReady = (raw: any) => {
      rpsStore.getState().setPlayerReady({
        match_id: raw.match_id,
        participant_id: raw.participant_id,
      });
    };

    const onSubRoundRevealed = (raw: any) => {
      rpsStore.getState().setSubRoundRevealed({
        match_id: raw.match_id,
        sub_round: raw.sub_round,
        player1_choice: raw.player1_choice,
        player2_choice: raw.player2_choice,
        result: raw.result,
        is_random_tiebreak: raw.is_random_tiebreak,
      });
    };

    const onMatchCompleted = (raw: any) => {
      rpsStore.getState().setMatchCompleted({
        match_id: raw.match_id,
        winner_id: raw.winner_id,
        loser_id: raw.loser_id,
        player1_games_won: raw.player1_games_won,
        player2_games_won: raw.player2_games_won,
        games_played: raw.games_played,
        was_tiebreak: raw.was_tiebreak,
      });
    };

    const onTournamentRoundCompleted = (raw: any) => {
      rpsStore.getState().setTournamentRoundCompleted({
        tournament_round: raw.tournament_round,
        advancing_participant_ids: raw.advancing_participant_ids,
        eliminated_participant_ids: raw.eliminated_participant_ids,
      });
    };

    const onTournamentEnded = (raw: any) => {
      rpsStore.getState().setTournamentEnded({
        champion_id: raw.champion_id,
        final_standings: raw.final_standings,
      });
    };

    socket.on(SERVER_EVENTS.BRACKET_DRAW_STARTED, onBracketDrawStarted);
    socket.on(SERVER_EVENTS.TOURNAMENT_ROUND_STARTED, onTournamentRoundStarted);
    socket.on(SERVER_EVENTS.SUB_ROUND_STARTED, onSubRoundStarted);
    socket.on(SERVER_EVENTS.PLAYER_READY, onPlayerReady);
    socket.on(SERVER_EVENTS.SUB_ROUND_REVEALED, onSubRoundRevealed);
    socket.on(SERVER_EVENTS.MATCH_COMPLETED, onMatchCompleted);
    socket.on(SERVER_EVENTS.TOURNAMENT_ROUND_COMPLETED, onTournamentRoundCompleted);
    socket.on(SERVER_EVENTS.TOURNAMENT_ENDED, onTournamentEnded);

    return () => {
      socket.off(SERVER_EVENTS.BRACKET_DRAW_STARTED, onBracketDrawStarted);
      socket.off(SERVER_EVENTS.TOURNAMENT_ROUND_STARTED, onTournamentRoundStarted);
      socket.off(SERVER_EVENTS.SUB_ROUND_STARTED, onSubRoundStarted);
      socket.off(SERVER_EVENTS.PLAYER_READY, onPlayerReady);
      socket.off(SERVER_EVENTS.SUB_ROUND_REVEALED, onSubRoundRevealed);
      socket.off(SERVER_EVENTS.MATCH_COMPLETED, onMatchCompleted);
      socket.off(SERVER_EVENTS.TOURNAMENT_ROUND_COMPLETED, onTournamentRoundCompleted);
      socket.off(SERVER_EVENTS.TOURNAMENT_ENDED, onTournamentEnded);
    };
  }, [roundId]);
}

/** Player gửi lựa chọn */
export function useRpsChoose(roundId: string) {
  return useCallback(
    (matchId: string, subRound: number, choice: 'rock' | 'paper' | 'scissors') => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      socket.emit(CLIENT_EVENTS.RPS_CHOOSE, {
        round_id: roundId,
        match_id: matchId,
        sub_round: subRound,
        choice,
      });
      rpsStore.getState().setMyChoiceSubmitted();
    },
    [roundId],
  );
}

/** Viewer/Player chọn trận spotlight */
export function useSpotlightMatch(roundId: string) {
  return useCallback(
    (matchId: string) => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      socket.emit(CLIENT_EVENTS.SPOTLIGHT_MATCH, { round_id: roundId, match_id: matchId });
      rpsStore.getState().setSpotlightMatch(matchId);
    },
    [roundId],
  );
}

/** Host force-complete round */
export function useHostForceComplete(roundId: string) {
  return useCallback(() => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.HOST_FORCE_COMPLETE_ROUND, { round_id: roundId });
  }, [roundId]);
}

/**
 * Host xác nhận bracket → bắt đầu timers cho tất cả trận.
 * Gọi sau khi admin xem bracket và bấm "▶ Bắt đầu thi đấu".
 */
export function useHostStartGames(roundId: string) {
  return useCallback(() => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.HOST_START_GAMES, { round_id: roundId });
  }, [roundId]);
}

/**
 * Host bắt đầu bốc thăm vòng tiếp theo (sau khi vòng hiện tại kết thúc).
 * Gọi sau khi admin xem kết quả vòng và bấm "Bắt đầu vòng tiếp theo".
 */
export function useHostStartNextRound(roundId: string) {
  return useCallback(() => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.HOST_START_NEXT_ROUND, { round_id: roundId });
  }, [roundId]);
}
